import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, X, ClipboardCheck, Heart, Activity, Settings, Trash2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  task_reminder: { icon: ClipboardCheck, color: '#22D3EE', label: 'Görev' },
  checkin_reminder: { icon: Heart, color: '#F472B6', label: 'Check-in' },
  vitals_reminder: { icon: Activity, color: '#EF4444', label: 'Vital' },
  health: { icon: Heart, color: '#34D399', label: 'Sağlık' },
  system: { icon: Settings, color: '#A78BFA', label: 'Sistem' },
  reminder: { icon: Bell, color: '#FBBF24', label: 'Hatırlatma' },
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/notifications`);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const markAllRead = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const deleteNotification = async (nid) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/notifications/${nid}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== nid));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const timeAgo = (isoStr) => {
    const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (diff < 60) return 'Az önce';
    if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
    return `${Math.floor(diff / 86400)} gün önce`;
  };

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-container">
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full transition-all hover:bg-white/5"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-white/50" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white"
            style={{ background: '#EF4444', boxShadow: '0 0 8px rgba(239,68,68,0.5)' }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-96 rounded-2xl overflow-hidden z-50 animate-fadeIn"
          style={{
            background: 'rgba(15,18,28,0.98)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            backdropFilter: 'blur(20px)',
          }}
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <span className="text-white/80 text-sm font-semibold">Bildirimler</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="flex items-center gap-1 text-[11px] text-cyan-400/70 hover:text-cyan-400 transition"
                data-testid="mark-all-read">
                <Check className="w-3 h-3" /> Tümünü oku
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 text-white/10 mx-auto mb-2" />
                <p className="text-white/20 text-xs">Henüz bildirim yok</p>
              </div>
            ) : (
              notifications.map(n => {
                const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.reminder;
                const Icon = cfg.icon;
                return (
                  <div key={n.notification_id}
                    className="group flex items-start gap-3 px-4 py-3 border-b border-white/[0.03] transition-all hover:bg-white/[0.02]"
                    style={{ opacity: n.read ? 0.5 : 1 }}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: `${cfg.color}15`, border: `1px solid ${cfg.color}20` }}>
                      <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/80 text-xs font-semibold leading-tight">{n.title}</p>
                      <p className="text-white/40 text-[11px] mt-0.5 leading-relaxed">{n.message}</p>
                      <p className="text-white/15 text-[10px] mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    <button onClick={() => deleteNotification(n.notification_id)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-white/5 transition shrink-0"
                      data-testid={`delete-notif-${n.notification_id}`}>
                      <X className="w-3 h-3 text-white/20" />
                    </button>
                    {!n.read && (
                      <div className="w-2 h-2 rounded-full shrink-0 mt-2" style={{ background: cfg.color, boxShadow: `0 0 6px ${cfg.color}60` }} />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
